import { Box, HStack, Heading, Slider, Text } from '@chakra-ui/react';

export interface NumberRangeSliderProps {
  /** Section label shown above the slider. */
  label: string;
  /** Optional icon shown before the label. */
  icon?: React.ReactNode;
  /** Minimum selectable value (inclusive). */
  min: number;
  /** Maximum selectable value (inclusive). */
  max: number;
  /** Step between discrete values (default 1). */
  step?: number;
  /**
   * Current exact-value filter array (compatible with `RiftboundFilter.cost` / `.might` / `.power`).
   * Empty array means "any" (no filter). A contiguous range [lo, lo+1, …, hi] is represented
   * here; a range covering the full [min, max] is treated the same as empty.
   */
  selected: number[];
  onChange: (vals: number[]) => void;
}

function rangeLabel(lo: number, hi: number, min: number, max: number): string {
  if (lo === min && hi === max) return 'Any';
  if (lo === hi) return String(lo);
  if (lo === min) return `≤ ${hi}`;
  if (hi === max) return `≥ ${lo}`;
  return `${lo} – ${hi}`;
}

/**
 * A two-thumb range slider for numeric card filters (Energy / Might / Power).
 *
 * Emits an array of all integer values in the selected range so it stays wire-compatible
 * with `RiftboundFilter.cost` / `.might` / `.power` (which the API receives as `?cost=1,2,3`).
 * Emits `[]` when the full [min, max] range is selected, meaning "no filter".
 */
export function NumberRangeSlider({
  label,
  icon,
  min,
  max,
  step = 1,
  selected,
  onChange,
}: NumberRangeSliderProps) {
  const lo = selected.length > 0 ? Math.min(...selected) : min;
  const hi = selected.length > 0 ? Math.max(...selected) : max;

  const isActive = selected.length > 0;
  const display = rangeLabel(lo, hi, min, max);

  const handleChange = (value: number[]) => {
    const [newLo, newHi] = value;
    if (newLo === min && newHi === max) {
      onChange([]);
      return;
    }
    const vals: number[] = [];
    for (let v = newLo; v <= newHi; v += step) vals.push(v);
    onChange(vals);
  };

  const ticks: number[] = [];
  for (let v = min; v <= max; v += step) ticks.push(v);
  const span = max - min || 1;

  return (
    <Box w="100%" display="flex" flexDirection="column" gap={1.5}>
      <HStack justify="space-between" align="baseline">
        <HStack gap={1.5} align="center">
          {icon && (
            <Box color="textMuted" lineHeight={1} flexShrink={0}>
              {icon}
            </Box>
          )}
          <Heading as="h3" size="sm">
            {label}
          </Heading>
        </HStack>
        <Text
          fontSize="xs"
          color={isActive ? 'accent' : 'textMuted'}
          fontWeight={isActive ? 'semibold' : 'normal'}
        >
          {display}
        </Text>
      </HStack>

      <Box px={2}>
        <Slider.Root
          colorPalette="brandGold"
          min={min}
          max={max}
          step={step}
          value={[lo, hi]}
          onValueChange={(e) => handleChange(e.value)}
          thumbAlignment="center"
        >
          <Slider.Control>
            <Slider.Track position="relative" overflow="visible">
              <Slider.Range />
              {ticks.map((v) => {
                const onRange = v >= lo && v <= hi;
                return (
                  <Box
                    key={v}
                    position="absolute"
                    top="50%"
                    left={`${((v - min) / span) * 100}%`}
                    transform="translate(-50%, -50%)"
                    w="4px"
                    h="4px"
                    borderRadius="full"
                    bg={onRange ? 'bg' : 'accent'}
                    pointerEvents="none"
                    zIndex={1}
                  />
                );
              })}
            </Slider.Track>
            <Slider.Thumbs />
          </Slider.Control>
        </Slider.Root>
        <HStack justify="space-between" mt={2.5} px="10px">
          <Text fontSize="xs" color="textMuted" lineHeight={1}>
            {min}
          </Text>
          <Text fontSize="xs" color="textMuted" lineHeight={1}>
            {max}+
          </Text>
        </HStack>
      </Box>
    </Box>
  );
}
